import { useState, useEffect } from 'react';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import RadioGroup from '@cloudscape-design/components/radio-group';
import Toggle from '@cloudscape-design/components/toggle';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Header from '@cloudscape-design/components/header';
import Alert from '@cloudscape-design/components/alert';
import { api, type RuleDef } from '../api';

interface RuleFormDrawerProps {
  visible: boolean;
  onDismiss: () => void;
  onSaved: () => void;
  /** If set, we're editing an existing rule */
  editingRule?: {
    ruleId: string;
    condition: { field: string; operator: string; value: string };
    action: { type: string; forwardTo?: string };
  };
}

const CONDITION_FIELDS = [
  { label: 'Sender domain is', value: 'from' },
  { label: 'Sender email is', value: 'sender' },
  { label: 'Subject contains', value: 'subject' },
  { label: 'Recipient is', value: 'recipient' },
];

const OPERATORS = [
  { label: 'Contains', value: 'contains' },
  { label: 'Equals', value: 'equals' },
  { label: 'Starts with', value: 'starts-with' },
  { label: 'Ends with', value: 'ends-with' },
  { label: 'Not equals', value: 'not-equals' },
];

/**
 * WM-12: Rule creation/edit form — right-side drawer with condition + action fields.
 */
export default function RuleFormDrawer({ visible, onDismiss, onSaved, editingRule }: RuleFormDrawerProps) {
  const [ruleName, setRuleName] = useState('');
  const [conditionField, setConditionField] = useState(CONDITION_FIELDS[0]);
  const [operator, setOperator] = useState(OPERATORS[0]);
  const [conditionValue, setConditionValue] = useState('');
  const [actionType, setActionType] = useState<'forward' | 'drop'>('forward');
  const [forwardTo, setForwardTo] = useState('');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (editingRule) {
      setRuleName(editingRule.ruleId);
      const field = CONDITION_FIELDS.find((f) => f.value === editingRule.condition.field) || CONDITION_FIELDS[0];
      setConditionField(field);
      const op = OPERATORS.find((o) => o.value === editingRule.condition.operator) || OPERATORS[0];
      setOperator(op);
      setConditionValue(editingRule.condition.value);
      setActionType(editingRule.action.type as 'forward' | 'drop');
      setForwardTo(editingRule.action.forwardTo || '');
    } else {
      resetForm();
    }
  }, [editingRule, visible]);

  function resetForm() {
    setRuleName('');
    setConditionField(CONDITION_FIELDS[0]);
    setOperator(OPERATORS[0]);
    setConditionValue('');
    setActionType('forward');
    setForwardTo('');
    setActive(true);
    setError('');
  }

  async function handleSubmit() {
    setError('');

    if (!ruleName.trim()) {
      setError('Rule name is required.');
      return;
    }
    if (!conditionValue.trim()) {
      setError('Condition value is required.');
      return;
    }
    if (actionType === 'forward' && !forwardTo.trim()) {
      setError('Forward destination is required.');
      return;
    }

    const def: RuleDef = {
      condition: {
        field: conditionField.value as RuleDef['condition']['field'],
        operator: operator.value as RuleDef['condition']['operator'],
        value: conditionValue.trim(),
      },
      action: actionType === 'drop' ? { type: 'drop' } : { type: 'forward', forwardTo: forwardTo.trim() },
    };

    setSaving(true);
    try {
      await api.putRule(ruleName.trim(), def);
      onSaved();
      onDismiss();
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rule. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        width: visible ? '480px' : '0',
        height: '100vh',
        backgroundColor: 'white',
        boxShadow: visible ? '-4px 0 12px rgba(0,0,0,0.1)' : 'none',
        zIndex: 1000,
        overflow: 'hidden',
        transition: 'width 0.2s ease',
      }}
    >
      {visible && (
        <div style={{ padding: '24px', height: '100%', overflowY: 'auto' }}>
          <SpaceBetween size="l">
            <Header
              variant="h2"
              actions={<Button iconName="close" variant="icon" ariaLabel="Close" onClick={onDismiss} />}
            >
              {editingRule ? 'Edit Rule' : 'Create Rule'}
            </Header>

            {error && <Alert type="error">{error}</Alert>}

            <Form
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={onDismiss}>
                    Cancel
                  </Button>
                  <Button variant="primary" loading={saving} onClick={handleSubmit}>
                    Save Rule
                  </Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="l">
                <FormField label="Rule Name" constraintText="Max 80 characters">
                  <Input
                    value={ruleName}
                    onChange={({ detail }) => setRuleName(detail.value)}
                    placeholder="e.g., Forward DHL to procurement"
                    disabled={!!editingRule}
                  />
                </FormField>

                <FormField label="Condition Type">
                  <Select
                    selectedOption={conditionField}
                    onChange={({ detail }) => setConditionField(detail.selectedOption as typeof conditionField)}
                    options={CONDITION_FIELDS}
                  />
                </FormField>

                <FormField label="Operator">
                  <Select
                    selectedOption={operator}
                    onChange={({ detail }) => setOperator(detail.selectedOption as typeof operator)}
                    options={OPERATORS}
                  />
                </FormField>

                <FormField label="Condition Value">
                  <Input
                    value={conditionValue}
                    onChange={({ detail }) => setConditionValue(detail.value)}
                    placeholder={
                      conditionField.value === 'from'
                        ? '@carrier.com'
                        : conditionField.value === 'subject'
                          ? 'invoice'
                          : 'ops-team@amazon.com'
                    }
                  />
                </FormField>

                <FormField label="Action">
                  <RadioGroup
                    value={actionType}
                    onChange={({ detail }) => setActionType(detail.value as 'forward' | 'drop')}
                    items={[
                      { value: 'forward', label: 'Forward to…' },
                      { value: 'drop', label: 'Drop (discard email)' },
                    ]}
                  />
                </FormField>

                {actionType === 'forward' && (
                  <FormField label="Forward Destination" constraintText="Must be a valid email address">
                    <Input
                      value={forwardTo}
                      onChange={({ detail }) => setForwardTo(detail.value)}
                      placeholder="team-lead@amazon.com"
                      type="email"
                    />
                  </FormField>
                )}

                <FormField label="Status">
                  <Toggle checked={active} onChange={({ detail }) => setActive(detail.checked)}>
                    {active ? 'Active' : 'Paused'}
                  </Toggle>
                </FormField>
              </SpaceBetween>
            </Form>
          </SpaceBetween>
        </div>
      )}
    </div>
  );
}
